import axios from 'axios'
import get from 'lodash.get'
import * as Sentry from '@sentry/browser'

import { getBrowserLocation } from '@utils/location'
import sortByDistance from '@utils/sort-by-distance'
import config from '@src/config'

export const state = {
  geolocationLoadingStatus: 'initial', // => "loading" => "loaded" || "error"
  nearestLocations: [],
  queryLoadingStatus: 'initial', // => "loading" => "loaded" || "error"
}

export const getters = {
  geolocationLoadingStatus: (state) => {
    return state.geolocationLoadingStatus
  },
  nearestLocations: (state) => {
    return state.nearestLocations
  },
  queryLoadingStatus: (state) => {
    return state.queryLoadingStatus
  },
}

export const mutations = {
  clearNearestLocations(state) {
    state.nearestLocations = []
  },
  setGeolocationLoadingStatus(state, status) {
    state.geolocationLoadingStatus = status
  },
  setNearestLocations(state, nearestLocations) {
    state.nearestLocations = nearestLocations
  },
  setQueryLoadingStatus(state, status) {
    state.queryLoadingStatus = status
  },
}

export const actions = {
  async handleGeolocate({ commit, dispatch }) {
    commit('setGeolocationLoadingStatus', 'loading')
    commit('clearNearestLocations')
    try {
      const coords = await getBrowserLocation()
      if (!coords || !coords.lat || !coords.lng) {
        dispatch(
          'ui/showToast',
          {
            toastMessage: 'Could not get device coordinates.',
            limit: true,
          },
          { root: true }
        )
        commit('setGeolocationLoadingStatus', 'error')
        return
      }

      await dispatch('showNearestEnabledLocations', { coords })
      commit('setGeolocationLoadingStatus', 'loaded')
    } catch (err) {
      console.log(err)

      dispatch(
        'ui/showToast',
        {
          toastMessage: 'Could not get device coordinates.',
          limit: true,
        },
        { root: true }
      )

      commit('setGeolocationLoadingStatus', 'error')
    }
  },
  async handleSubmitQuery({ commit, dispatch }, query) {
    commit('setQueryLoadingStatus', 'loading')
    commit('clearNearestLocations')

    const geocodingKey = config.geocodingKey
    if (!geocodingKey) {
      dispatch(
        'ui/showToast',
        {
          toastMessage: 'There was a problem fetching the locations.',
          limit: true,
        },
        { root: true }
      )
      throw new Error('no geocoding key')
    }
    const formattedQuery = `${query
      .trim()
      .split(' ')
      .join('+')},+uk`
    const url = 'https://maps.googleapis.com/maps/api/geocode/json'

    try {
      const res = await axios.get(
        `${url}?address=${formattedQuery}&key=${geocodingKey}`
      )

      if (res.data.error_message === 'The provided API key is invalid.') {
        dispatch(
          'ui/showToast',
          {
            toastMessage: 'There was a problem fetching the locations.',
            limit: true,
          },
          { root: true }
        )
        throw new Error('invalid api key')
      }

      const coords = get(res, 'data.results[0].geometry.location')
      if (!coords || !coords.lat || !coords.lng) {
        dispatch(
          'ui/showToast',
          {
            toastMessage:
              'Could not find any locations matching your search term.',
            limit: true,
          },
          { root: true }
        )
        commit('setQueryLoadingStatus', 'error')
        return
      }

      await dispatch('showNearestEnabledLocations', { coords })
      commit('setQueryLoadingStatus', 'loaded')
    } catch (err) {
      console.log(err)
      Sentry.withScope((scope) => {
        scope.setTag('Location - handleSubmitQuery')
        Sentry.captureException(new Error(err))
      })
      commit('setQueryLoadingStatus', 'error')
    }
  },
  async showNearestEnabledLocations(
    { commit, dispatch, rootGetters },
    { coords }
  ) {
    try {
      const opts = {
        xName: 'latitude',
        yName: 'longitude',
        seperator: 'fields',
      }
      const origin = { latitude: coords.lat, longitude: coords.lng }
      const restaurantList = rootGetters['contentful/locationsEnabled']

      const nearestLocations = await sortByDistance(
        origin,
        restaurantList,
        opts
      ).slice(0, 3)

      commit('setNearestLocations', nearestLocations)
      commit('ui/toggleLocationModal', { modalIsVisible: true }, { root: true })
    } catch (err) {
      dispatch(
        'ui/showToast',
        {
          toastMessage: 'Could not find any nearby locations.',
          limit: true,
        },
        { root: true }
      )

      console.log(err)
    }
  },
  setNearestLocations({ commit }, locations) {
    commit('setNearestLocations', locations)
  },
}
